import styled from 'styled-components';

import { Trans, useTranslation } from 'react-i18next';
import React from 'react';
import { FlexRow, useMatchMedia } from '@make-software/cspr-design';
import { TimeFrameType, TimeFrameTypes } from './chart.types';
import {
  BodyText,
  formatDateShort,
  formatDateWithMonthAndYear,
} from '@block-explorer/ui';

export const ChartBlock = styled.div(({ theme }) =>
  theme.withMedia({
    display: 'flex',
    width: ['100%', '100%', '100%'],
    flexDirection: 'column',
    flexShrink: 0,
    alignItems: ['center', 'center', 'flex-start'],
    backgroundColor: theme.styleguideColors.backgroundPrimary,
    padding: ['20px 16px 32px', '20px 20px 36px'],
  })
);

export const TitleContainer = styled.div(({ theme }) =>
  theme.withMedia({
    display: 'flex',
    flexDirection: ['column', 'column', 'row'],
    alignItems: ['flex-start', 'flex-start', 'center'],
    justifyContent: 'space-between',
    width: '100%',
    '& > * + *': {
      marginTop: [32, 32, 0],
    },
  })
);

export const StyledTimeFrameWrapper = styled.div<{ active: boolean }>(
  ({ theme, active }) =>
    theme.withMedia({
      border: 'none',
      cursor: 'pointer',
      color: theme.styleguideColors.contentPrimary,
      background: theme.styleguideColors.fillSecondary,
      borderRadius: theme.borderRadius.base,
      fontWeight: theme.typography.fontWeight.regular,
      minHeight: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: ['4px 12px'],
      ':hover': {
        background: theme.styleguideColors.fillSecondaryBlueHover,
      },
      ':active': {
        background: theme.styleguideColors.fillSecondaryBlueHover,
        fontWeight: theme.typography.fontWeight.semiBold,
      },
      ...(active && {
        background: theme.styleguideColors.fillSecondaryBlueHover,
        fontWeight: theme.typography.fontWeight.semiBold,
      }),
    })
);

export const timeFrameValues: TimeFrameType[] = [
  { label: TimeFrameTypes.week, days: 7, formatter: formatDateShort },
  { label: TimeFrameTypes.month, days: 30, formatter: formatDateShort },
  { label: TimeFrameTypes.quarter, days: 90, formatter: formatDateShort },
  {
    label: TimeFrameTypes.year,
    days: 365,
    formatter: formatDateWithMonthAndYear,
  },
  {
    label: TimeFrameTypes.all,
    days: 9999,
    formatter: formatDateWithMonthAndYear,
  },
];

interface TimeFrameButtonProps {
  value: string;
  onClick: (event: any) => void;
  onKeyDown: (event: any) => void;
  active: boolean;
}

export const TimeFrameButton = ({
  value,
  onClick,
  onKeyDown,
  active,
}: TimeFrameButtonProps) => {
  return (
    <StyledTimeFrameWrapper
      onClick={onClick}
      active={active}
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      <BodyText size={active ? 1 : 3} variation="black">
        {value}
      </BodyText>
    </StyledTimeFrameWrapper>
  );
};

export const TimeFrameButtonsGroup = ({ handleChange, activeTimeFrame }) => {
  const { t } = useTranslation();
  const screenType = useMatchMedia(['mobile', 'tabletOrAbove'], []);

  return (
    <FlexRow justify={'end'} align={'center'} itemsSpacing={12}>
      {screenType === 'tabletOrAbove' && (
        <BodyText variation="lightGray" size={3}>
          <Trans t={t}>Time frame</Trans>
        </BodyText>
      )}
      <FlexRow itemsSpacing={4}>
        {timeFrameValues.map((item) => (
          <TimeFrameButton
            onClick={() => handleChange(item)}
            value={item.label}
            active={item.label === activeTimeFrame.label}
            key={item.label}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') {
                handleChange(item);
              }
            }}
          />
        ))}
      </FlexRow>
    </FlexRow>
  );
};
